import React, { useEffect } from 'react';
import L from 'leaflet';
import { MapContainer, useMap } from 'react-leaflet';
import { Link } from '@tanstack/react-router';
import { ChevronDownIcon, FileTextIcon } from 'lucide-react';
import { GpsLocationDto } from '@area-restrita/api-client/model/gpsLocationDto';
import { BuslineScheduleDetailQueryResponse } from '@area-restrita/api-client/model/buslineScheduleDetailQueryResponse';
import { abbreviateDay } from '@area-restrita/shared/busline/busline-schedule';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import PageContainer from '@/components/layout/PageContainer';
import CargoTileLayer from '@/components/Map/CargoTileLayer';
import RouteMapLayer from '@/components/Map/RouteMapLayer';
import { RoutePath } from '@/utils/lineStringToRoute';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import BusTrackingCard from '../../components/BusTrackingCard';
import BusMarker from '../../components/BusMarker';

type Documents = BuslineScheduleDetailQueryResponse['documents'];
type DaysOfWeek = BuslineScheduleDetailQueryResponse['daysOfWeek'];

interface IProps {
  location: GpsLocationDto | null;
  routeName: string;
  driverName?: string;
  route: RoutePath | null;
  active: boolean;
  startAt: string;
  endAt: string;
  daysOfWeek: DaysOfWeek;
  documents: Documents;
}

const DocumentAction: React.FC<{ documents: Documents }> = ({ documents }) => {
  if (documents.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">
        Nenhum documento vinculado
      </span>
    );
  }

  if (documents.length === 1) {
    return (
      <Link
        to="/documents/$companyDocumentPublicId"
        params={{ companyDocumentPublicId: documents[0].publicId }}
      >
        <Button size="sm" className="mt-0">
          <FileTextIcon className="h-4 w-4" />
          Ver documento
        </Button>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="mt-0">
          <FileTextIcon className="h-4 w-4" />
          Documentos
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[1001]">
        <DropdownMenuGroup>
          {documents.map((document) => (
            <DropdownMenuItem key={document.id} asChild>
              <Link
                to="/documents/$companyDocumentPublicId"
                params={{ companyDocumentPublicId: document.publicId }}
              >
                {document.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RouteBounds: React.FC<{ route: RoutePath | null }> = ({ route }) => {
  const map = useMap();

  useEffect(() => {
    if (!route) return;

    map.fitBounds(route.path, { padding: [48, 48] });
  }, [map, route]);

  return null;
};

const ScheduleInfoPanel: React.FC<
  Pick<
    IProps,
    | 'active'
    | 'daysOfWeek'
    | 'documents'
    | 'driverName'
    | 'endAt'
    | 'location'
    | 'routeName'
    | 'startAt'
  >
> = ({
  active,
  daysOfWeek,
  documents,
  driverName,
  endAt,
  location,
  routeName,
  startAt,
}) => {
  const detailsContent = (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {daysOfWeek.map((dayOfWeek) => (
          <Badge key={dayOfWeek}>{abbreviateDay(dayOfWeek)}</Badge>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4">
        <span className="text-sm font-medium">Documentos</span>
        <DocumentAction documents={documents} />
      </div>
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4 xl:max-w-md">
      {active ? (
        <BusTrackingCard
          routeName={routeName}
          driverName={driverName}
          location={location}
          details={
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                {startAt} às {endAt}
              </div>
              {detailsContent}
            </div>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{routeName}</CardTitle>
              <CardDescription>
                {startAt} às {endAt}
              </CardDescription>
            </CardHeader>
            <CardContent>{detailsContent}</CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground">
              Rastreamento ao vivo disponível apenas durante o horário de
              execução.
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const BuslineMapView = React.forwardRef<L.Map, IProps>(
  (
    {
      location,
      routeName,
      driverName,
      route,
      active,
      startAt,
      endAt,
      daysOfWeek,
      documents,
    },
    mapRef,
  ) => (
    <PageContainer
      breadcrumbItems={[
        { label: 'Linhas e Horários', to: '/busline-schedules' },
      ]}
    >
      <div className="relative h-full flex flex-col gap-4 xl:block">
        <div className="xl:z-[1000] flex w-full justify-end xl:pointer-events-none xl:absolute xl:right-0 xl:top-0 ">
          <div className="w-full xl:pointer-events-auto xl:w-auto xl:m-4">
            <ScheduleInfoPanel
              active={active}
              daysOfWeek={daysOfWeek}
              documents={documents}
              driverName={driverName}
              endAt={endAt}
              location={location}
              routeName={routeName}
              startAt={startAt}
            />
          </div>
        </div>

        <Card className="relative h-[60vh] min-h-[360px] w-full overflow-hidden xl:h-full xl:min-h-[70vh]">
          <MapContainer
            center={
              location
                ? { lat: location.lat, lng: location.lng }
                : [-23.5505, -46.6333]
            }
            zoom={16}
            scrollWheelZoom={true}
            style={{ zIndex: 0, height: '100%' }}
            ref={mapRef}
          >
            <CargoTileLayer />

            {location && (
              <BusMarker position={{ lat: location.lat, lng: location.lng }} />
            )}

            {route && <RouteMapLayer route={route} />}
            <RouteBounds route={route} />
          </MapContainer>

          {!route && (
            <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/80 p-6 text-center">
              <div className="max-w-sm rounded-md border bg-background p-4 text-sm text-muted-foreground shadow-sm">
                Nenhum desenho de rota disponível para esta linha.
              </div>
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  ),
);

export default BuslineMapView;
